import Plot from 'react-plotly.js'
import { Data } from 'plotly.js'

interface RiskItem {
  id: string
  label: string
  probability: number // 0-1
  impact: number // 0-1
  category?: string
}

interface RiskMatrixProps {
  data: RiskItem[]
  title?: string
  height?: number
  onItemClick?: (item: RiskItem) => void
  className?: string
}

export default function RiskMatrix({
  data,
  title,
  height = 400,
  onItemClick,
  className
}: RiskMatrixProps) {
  // Color based on risk level (probability * impact)
  const getColor = (probability: number, impact: number) => {
    const risk = probability * impact
    if (risk > 0.6) return '#ef4444' // Critical
    if (risk > 0.3) return '#f59e0b' // Warning
    return '#10b981' // Low risk
  }

  const trace: Data = {
    type: 'scatter',
    mode: 'markers',
    x: data.map(d => d.probability * 100),
    y: data.map(d => d.impact * 100),
    text: data.map(d => d.label),
    textposition: 'top center',
    marker: {
      size: 20,
      color: data.map(d => getColor(d.probability, d.impact)),
      opacity: 0.8,
      line: { width: 2, color: '#1e293b' }
    },
    hovertemplate: '<b>%{text}</b><br>Probability: %{x:.0f}%<br>Impact: %{y:.0f}%<extra></extra>'
  } as Data

  // Background zones
  const shapes = [
    // Low risk zone (green)
    {
      type: 'rect' as const,
      x0: 0, y0: 0, x1: 50, y1: 50,
      fillcolor: '#10b981',
      opacity: 0.1,
      line: { width: 0 }
    },
    // Medium risk zone (yellow)
    {
      type: 'rect' as const,
      x0: 50, y0: 0, x1: 100, y1: 50,
      fillcolor: '#f59e0b',
      opacity: 0.1,
      line: { width: 0 }
    },
    {
      type: 'rect' as const,
      x0: 0, y0: 50, x1: 50, y1: 100,
      fillcolor: '#f59e0b',
      opacity: 0.1,
      line: { width: 0 }
    },
    // High risk zone (red)
    {
      type: 'rect' as const,
      x0: 50, y0: 50, x1: 100, y1: 100,
      fillcolor: '#ef4444',
      opacity: 0.1,
      line: { width: 0 }
    }
  ]

  const layout = {
    title: title ? {
      text: title,
      font: { color: '#e2e8f0', size: 14 }
    } : undefined,
    height,
    margin: { t: title ? 50 : 30, r: 30, b: 50, l: 60 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { color: '#94a3b8', family: 'Inter, sans-serif' },
    xaxis: {
      title: { text: 'Probability (%)', font: { color: '#94a3b8', size: 12 } },
      range: [0, 100],
      tickfont: { size: 10 },
      gridcolor: '#334155',
      zeroline: false
    },
    yaxis: {
      title: { text: 'Impact (%)', font: { color: '#94a3b8', size: 12 } },
      range: [0, 100],
      tickfont: { size: 10 },
      gridcolor: '#334155',
      zeroline: false
    },
    shapes,
    showlegend: false,
    hovermode: 'closest' as const
  }

  const config = {
    displayModeBar: false,
    responsive: true
  }

  return (
    <div className={className}>
      <Plot
        data={[trace]}
        layout={layout}
        config={config}
        style={{ width: '100%' }}
        onClick={(event) => {
          if (event.points && event.points[0]) {
            const index = event.points[0].pointIndex
            onItemClick?.(data[index])
          }
        }}
      />
    </div>
  )
}
